import { createCustomFilterForEntity } from "@shared/backend/create-custom-filter-for-entity";
import {
  type GroupVolunteerRegion,
} from "@shared/helpers/group-volunteer-regions";
import {
  filterWithRelationsAndJson,
  type RelationalOrJsonEntityFilter,
} from "@shared/helpers/sql-relations";
import { dbNamesOf, SqlDatabase } from "remult";
import { Group } from "./";

const createCustomFilter = createCustomFilterForEntity<Group>();

export class GroupFilters {
  /**
   * # 🌟✨ HyperFilter – The Ultimate Filtering Powerhouse!
   * ### 🤝 Filter through complex relations effortlessly
   * ### 📦 Directly filter JSON fields (no backend work required!)
   * ### 🚀 Transforms your filter into a single, optimized DB query
   * ### 🔒 Engineered for maximum SQL injection protection
   *
   * ---
   * ### Example:
   * ```ts
   * myEntityRepo.find({
   *  where: MyEntity.hyperFilter({ ... })
   * })
   * ```
   *
   * ---
   * Built by Natan Salmon
   */
  static hyperFilter = createCustomFilter<RelationalOrJsonEntityFilter<Group>>(
    (filters) => {
      return filterWithRelationsAndJson(Group)(filters);
    },
  );

  static get dbNames() {
    return dbNamesOf(Group, { tableName: true });
  }

  static filterGroupsByEventDate = createCustomFilter<{ date: Date }>(
    ({ date }) => {
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0); // Start of the day
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999); // End of the day
      return GroupFilters.hyperFilter({
        opportunities: {
          $some: {
            opportunity: {
              $is: {
                $and: [
                  { startDate: { $gte: startOfDay } },
                  { endDate: { $lte: endOfDay } },
                ],
              },
            },
          },
        },
      });
    },
  );

  /** קבוצות שבחרו לפחות אחד מהאזורים (OR). */
  static filterByPreferredVolunteerRegions = createCustomFilter<{
    regions: GroupVolunteerRegion[];
  }>(({ regions }) => {
    if (!regions.length) {
      return {};
    }
    return SqlDatabase.rawFilter(async (build) => {
      const dbNames = await GroupFilters.dbNames;
      const parts = regions.map(
        (region) =>
          `${dbNames.preferredVolunteerRegions} @> ${build.param(JSON.stringify([region]))}::jsonb`,
      );
      build.sql = `(${parts.join(" OR ")})`;
    });
  });
}
